import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "@/lib/api-client";
import type { CrmActionObservedOutcome, CrmActionOutcomeScoring } from "./use-crm-event-action-score";

export interface CrmActionDecisionFilters {
  actionRef?: string;
  contractRef?: string;
  sourceEventType?: string;
  signalFamily?: string;
}

export interface CrmActionDecisionBucket {
  accepted: number;
  dismissed: number;
  total: number;
}

export interface CrmActionDecisionNamedBucket extends CrmActionDecisionBucket {
  actionRef?: string;
  intent?: string;
  readinessStatus?: string;
  executionPlanStatus?: string;
  sourceSurface?: string;
  sourceEventType?: string;
  signalFamily?: string;
  actorType?: string;
}

export interface CrmActionDecision {
  id: string;
  contactId: string | null;
  targetEntityType?: string | null;
  targetEntityId?: string | null;
  recommendationId: string;
  actionRef: string;
  intent: string;
  decision: "accepted" | "dismissed";
  readinessStatus: string;
  executionPlanStatus: string;
  executionPlanMode: string | null;
  executionPlanReason: string | null;
  confidence: number | null;
  observedOutcome: CrmActionObservedOutcome | null;
  outcomeScoring: CrmActionOutcomeScoring | null;
  materialization: {
    id: string;
    materializedEntityType: string | null;
    materializedEntityId: string | null;
    materializationStatus: string | null;
    manualResolution: Record<string, unknown> | null;
    materializedAt: string;
  } | null;
  sourceEventId: string | null;
  sourceEventType?: string | null;
  signalFamily?: string | null;
  sourceSurface: string | null;
  actorId: string;
  actorType: string;
  decidedAt: string;
}

export interface CrmActionDecisionSummary {
  windowDays: number;
  filters: {
    actionRef: string | null;
    contractRef: string | null;
    sourceEventType: string | null;
    signalFamily: string | null;
  };
  totals: CrmActionDecisionBucket;
  byActionRef: CrmActionDecisionNamedBucket[];
  byIntent: CrmActionDecisionNamedBucket[];
  byReadiness: CrmActionDecisionNamedBucket[];
  byExecutionPlan: CrmActionDecisionNamedBucket[];
  bySourceSurface: CrmActionDecisionNamedBucket[];
  bySourceEventType: CrmActionDecisionNamedBucket[];
  bySignalFamily: CrmActionDecisionNamedBucket[];
  byActorType: CrmActionDecisionNamedBucket[];
  automation: CrmActionDecisionNamedBucket;
  staff: CrmActionDecisionNamedBucket;
  recent: CrmActionDecision[];
}

export function useCrmActionDecisions(filters: CrmActionDecisionFilters = {}) {
  const [data, setData] = useState<CrmActionDecisionSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDecisions = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      const actionRef = filters.actionRef ?? filters.contractRef;
      if (actionRef) {
        params.set("actionRef", actionRef);
      }
      if (filters.sourceEventType) {
        params.set("sourceEventType", filters.sourceEventType);
      }
      if (filters.signalFamily) {
        params.set("signalFamily", filters.signalFamily);
      }
      const query = params.toString();
      const response = await apiFetch<{ data: CrmActionDecisionSummary }>(
        `/crm/intelligence/action-recommendations/decisions${query ? `?${query}` : ""}`,
      );
      setData(response.data);
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : "Failed to load action decisions");
    } finally {
      setIsLoading(false);
    }
  }, [filters.actionRef, filters.contractRef, filters.signalFamily, filters.sourceEventType]);

  useEffect(() => {
    fetchDecisions();
  }, [fetchDecisions]);

  return { data, isLoading, error, refetch: fetchDecisions };
}
